import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Uses DOMPurify to remove potentially dangerous HTML/JavaScript
 *
 * @param dirty - Raw HTML string that may contain malicious code
 * @returns Sanitized HTML string safe for rendering
 */
export const sanitizeHtml = (dirty: string): string => {
  if (!dirty) return '';

  // Configure DOMPurify with safe defaults
  const config = {
    // Allow common safe HTML tags
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr',
      'strong', 'em', 'u', 'b', 'i',
      'ul', 'ol', 'li',
      'a', 'img',
      'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span'
    ],

    // Allow safe attributes
    ALLOWED_ATTR: [
      'href', 'target', 'rel',
      'src', 'alt', 'title', 'width', 'height',
      'class', 'id'
    ],

    // Allow only safe URL schemes
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,

    // Forbid tags that could be used for attacks
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],

    // Forbid event handler attributes
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],

    // Return as string
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  };

  // Sanitize and return
  return DOMPurify.sanitize(dirty, config);
};

/**
 * Sanitizes plain text by escaping HTML entities
 * Use this when you want to display user input as plain text
 *
 * @param text - Raw text that may contain HTML characters
 * @returns HTML-escaped text safe for display
 */
export const escapeHtml = (text: string): string => {
  if (!text) return '';

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};
